import { find, findIndex, includes } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { cascadesApi, shopsApi } from 'api';
import {
  CrudPage,
  CrudTableAction,
  CrudTableActionType,
  DataGridColumnDefinition,
  SidebarLayout,
  SidebarSize,
  dataGridColumns,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, QueryKey, RequisitesCascadeMode } from 'enums';
import {
  useMutation,
  usePartialQuery,
  useShopsQuery,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Cascade, CascadesFilters, FilterDefinition } from 'types';
import { formUtils } from 'utils';

import { CascadeDetails } from '../CascadeDetails';

export const CascadesList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.cascades_list.table',
  });
  const { t: tRemoveDialog } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.cascades_list.remove_dialog',
  });
  const { t: tTitle } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades',
  });

  const { role, isAdmin, isTechOperator, isAdminOrTechOperator } = useUser();
  const { p2pProviders, cascades: allCascades } = useUserContext();

  const queryClient = useQueryClient();
  const queryResult = usePartialQuery(QueryKey.Cascades, cascadesApi.getAll, {
    enabled: isAdminOrTechOperator,
  });
  const { data: cascades = [] } = queryResult || [];

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const { mutate: remove } = useMutation(cascadesApi.remove, {
    onSuccess: (data) => {
      if (data.id === selectedCascade?.id) {
        setSelectedCascade(null);
      }
      queryClient.invalidateQueries(QueryKey.Cascades);
    },
    notifierType: 'remove',
  });

  const [selectedCascade, setSelectedCascade] = useState<Cascade | null>(null);
  const handleOpenCascade = useCallback(
    (cascade: Cascade) => setSelectedCascade(cascade),
    [],
  );
  const handleCloseCascade = useCallback(() => setSelectedCascade(null), []);

  const cascadeDetailsPage = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.CASCADE_DETAILS;
    } else if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.CASCADE_DETAILS;
    }
  }, [isAdmin, isTechOperator]);

  const handleCreateOrEdit = useCallback(
    (id: string) => navigate(generatePath(cascadeDetailsPage!, { id })),
    [cascadeDetailsPage, navigate],
  );

  const actions = useMemo(
    (): CrudTableAction<Cascade>[] => [
      {
        type: CrudTableActionType.Details,
        onClick: (item) => handleCreateOrEdit(item.id),
      },
      {
        type: CrudTableActionType.Remove,
        renderDialogContent: (item) => (
          <div>
            {tRemoveDialog('confirm')}
            <div>{item.name}</div>
          </div>
        ),
        onRemove: (item, { close }) =>
          remove(item.id, {
            onSuccess: close,
          }),
      },
    ],
    [remove, handleCreateOrEdit, tRemoveDialog],
  );

  const getCascadeModeLabel = useCallback(
    (mode: RequisitesCascadeMode) => {
      switch (mode) {
        case RequisitesCascadeMode.TradersFirst:
          return t('modes.traders_first');
        case RequisitesCascadeMode.ProvidersFirst:
          return t('modes.providers_first');
        case RequisitesCascadeMode.TradersOnly:
          return t('modes.traders_only');
        case RequisitesCascadeMode.ProvidersOnly:
          return t('modes.providers_only');
        case RequisitesCascadeMode.Parallel:
          return t('modes.parallel');
        case RequisitesCascadeMode.ParallelPriority:
          return t('modes.parallel_priority');
        case RequisitesCascadeMode.SequentialAssignees:
          return t('modes.sequential_assignees');
        case RequisitesCascadeMode.SequentialProviders:
          return t('modes.sequential_providers');
        default:
          return mode;
      }
    },
    [t],
  );

  const getCascadeModeLabelPayout = useCallback(
    (mode: RequisitesCascadeMode) => {
      switch (mode) {
        case RequisitesCascadeMode.TradersFirst:
          return t('modes.default');
        default:
          return getCascadeModeLabel(mode);
      }
    },
    [t, getCascadeModeLabel],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<Cascade>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('columns.name'),
        valueGetter: (item) => item.name || '',
      },
      {
        header: t('columns.payin_mode'),
        valueGetter: (item) => (
          <Fragment>
            {getCascadeModeLabel(item.payinCascadeMode)}
            {item.payinCascadeMode !== RequisitesCascadeMode.TradersOnly && (
              <div>{`${t('columns.p2p_providers_count')}: ${
                item.p2pProvidersConfigs?.length || 0
              }`}</div>
            )}
          </Fragment>
        ),
      },
      {
        header: t('columns.payout_mode'),
        valueGetter: (item) => (
          <Fragment>
            {getCascadeModeLabelPayout(item.payoutCascadeMode)}
            {item.payoutCascadeMode !== RequisitesCascadeMode.TradersOnly &&
              item.payoutCascadeMode !==
                RequisitesCascadeMode.SequentialAssignees && (
                <div>{`${t('columns.p2p_providers_count')}: ${
                  item.payoutP2PProvidersConfigs?.length || 0
                }`}</div>
              )}
            {item.payoutCascadeMode ===
              RequisitesCascadeMode.SequentialAssignees && (
              <div>{`${t('columns.p2p_assignees_count')}: ${
                item.payoutOrderAssignees?.length || 0
              }`}</div>
            )}
          </Fragment>
        ),
      },
    ],
    [t, getCascadeModeLabel, getCascadeModeLabelPayout],
  );

  const filtersDefinitions: FilterDefinition<CascadesFilters>[] = useMemo(
    () => [
      {
        label: t('filters.cascade'),
        name: 'id',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(allCascades),
        getDisplayName: (groupId: string) =>
          find(allCascades, { id: groupId })?.name,
      },
      {
        label: t('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: t('filters.p2p_provider'),
        name: 'p2pProviderId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(p2pProviders),
        getDisplayName: (value: string) =>
          find(p2pProviders, { id: value })?.name,
      },
      {
        label: t('columns.payin_mode'),
        name: 'payinCascadeMode',
        type: FilterDefinitionType.Enum,
        enum: Object.values(RequisitesCascadeMode),
        getDisplayName: getCascadeModeLabel,
      },
      {
        label: t('columns.payout_mode'),
        name: 'payoutCascadeMode',
        type: FilterDefinitionType.Enum,
        enum: Object.values(RequisitesCascadeMode).filter((value) =>
          includes(
            [
              RequisitesCascadeMode.TradersFirst,
              RequisitesCascadeMode.TradersOnly,
              RequisitesCascadeMode.ProvidersOnly,
            ],
            value,
          ),
        ),
        getDisplayName: getCascadeModeLabelPayout,
      },
    ],
    [
      t,
      queryResultShops.data,
      p2pProviders,
      allCascades,
      getCascadeModeLabel,
      getCascadeModeLabelPayout,
    ],
  );

  return (
    <Fragment>
      <SidebarLayout
        sidebarProps={{
          size: SidebarSize.md,
          open: !!selectedCascade,
          onClose: handleCloseCascade,
          title: t('details.title'),
          content: <CascadeDetails cascade={selectedCascade} />,
        }}
      >
        <CrudPage
          header={{
            title: tTitle('title'),
            rightContentButton: {
              onClick: () => handleCreateOrEdit(NEW_ID),
            },
          }}
          table={{
            queryResult,
            columns,
            onRowClick: handleOpenCascade,
            selectedRowIndex: findIndex(cascades, {
              id: selectedCascade?.id,
            }),
            actions,
          }}
          filters={{ filtersDefinitions }}
        />
      </SidebarLayout>
    </Fragment>
  );
};
